import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
} from '@mui/material';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import Scrollbar from '../../../components/Scrollbar';
import { AccountTypeConversion } from '../../../client/services';
import moment from 'moment';

interface ConversionLog {
  id: string;
  userId: string;
  fromRole: string;
  toRole: string;
  convertedBy: string;
  convertedAt: string;
  reason: string | null;
  previousData: any;
  newData: any;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  converter?: {
    id: string;
    name: string;
    email: string;
  };
}

const AccountTypeConversionHistory = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ConversionLog[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchConversionLogs();
  }, [page, limit]);

  const fetchConversionLogs = async () => {
    try {
      setLoading(true);
      const response = await AccountTypeConversion.find({
        query: {
          $limit: limit,
          $skip: page * limit,
          $sort: { convertedAt: -1 },
        },
      });
      setData(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Error fetching conversion logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Page title="Account Type Conversion History">
      <Container maxWidth="xl">
        <Card>
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Account Type Conversion History
            </Typography>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1200, position: 'relative' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">User</TableCell>
                      <TableCell align="center">From Role</TableCell>
                      <TableCell align="center">To Role</TableCell>
                      <TableCell align="center">Converted By</TableCell>
                      <TableCell align="center">Converted At</TableCell>
                      <TableCell align="center">Reason</TableCell>
                      <TableCell align="center">Previous Data</TableCell>
                      <TableCell align="center">New Data</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                        >
                          No conversion records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell align="center">
                            {log.user ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {log.user.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.user.email}
                                </Typography>
                              </Box>
                            ) : log.previousData?.name ? (
                              <Typography variant="body2" fontWeight="bold">
                                {log.previousData.name}
                              </Typography>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={log.fromRole}
                              color="default"
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={log.toRole}
                              color="primary"
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {log.converter ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {log.converter.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.converter.email}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Admin
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {moment(log.convertedAt).format('YYYY-MM-DD HH:mm:ss')}
                          </TableCell>
                          <TableCell align="center">
                            {log.reason ? (
                              <Tooltip title={log.reason} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {log.reason}
                                </Typography>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {log.previousData ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {log.previousData.name || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Role: {log.previousData.userRole || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Approved: {log.previousData.isApproved ? 'Yes' : 'No'}
                                </Typography>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {log.newData ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {log.newData.name || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Role: {log.newData.userRole || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Approved: {log.newData.isApproved ? 'Yes' : 'No'}
                                </Typography>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={limit}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default AccountTypeConversionHistory;

